import { Component, Injector, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * CountrySpecificsHighestAndBestUse Widget
 */
@Component({
  selector: 'country-specifics-highest-and-best-use-widget',
  templateUrl: './country-specifics-highest-and-best-use-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../../common/css/text-and-list.scss', './country-specifics-highest-and-best-use-widget.component.scss']
})
export class CountrySpecificsHighestAndBestUseWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Should custom labels be disabled?
   */
  disableCustomLabels = true;

  /**
   * Delete current use row subject
   */
  private deleteCurrentRowSubjectUse = new Subject<void>();

  /**
   * Delete current use row observable
   */
  public deleteCurrentRowUse$ = this.deleteCurrentRowSubjectUse.asObservable();

  /**
   * Add row subject
   */
  private addRowSubjectUse = new Subject<any>();

  /**
   * Add use row observable
   */
  public addRowUse$ = this.addRowSubjectUse.asObservable();

  /**
   * Move current row up subject
   */
  private moveCurrentRowUpSubjectUse = new Subject<void>();

  /**
   * Move current use row up observable
   */
  public moveCurrentRowUpUse$ = this.moveCurrentRowUpSubjectUse.asObservable();

  /**
   * Move current row down subject
   */
  private moveCurrentRowDownSubjectUse = new Subject<void>();

  /**
   * Move current row down observable
   */
  public moveCurrentRowDownUse$ = this.moveCurrentRowDownSubjectUse.asObservable();

  /**
   * Text & List use section rows
   */
  rowsUse: any = [];

  /**
   * Text & List use section name
   */
  readonly textAndListSectionNameUse: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'countrySpecificsHighestAndBestUse', false);
    this.textAndListSectionNameUse = `${this.name}-use`;
  }

  /**
   * Widget's text area options (if used)
   */
  textAreaOptions: any = null;

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("countrySpecificsHighestAndBestUse", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.initializeUseSection();

    this.loadReflist('ref_l3_country_specifics_use', !this.disableCustomLabels);

    this.mockupData$ = this.widgetService.loadMockup('countrySpecificsHighestAndBestUse').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('countrySpecificsHighestAndBestUse', this.getReport()).pipe();
  }

  /**
   * Initializes the Text & List use section
   */
  private initializeUseSection() {
    this.rowsUse = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionNameUse, [
      {
        topicId: 1,
        fieldName: 'mostProbableBuyer',
        isTextField: true,
        lastTopicId: 1,
        selected: true
      }, {
        topicId: 2,
        fieldName: 'asVanant',
        isTextField: true,
        lastTopicId: 2,
        selected: true
      }, {
        topicId: 3,
        fieldName: 'asImproved',
        isTextField: true,
        lastTopicId: 3,
        selected: true
      }, {
        topicId: 4,
        fieldName: 'useOfRealEstateAsOfDateOfValue',
        isTextField: true,
        lastTopicId: 4,
        selected: true
      }, {
        topicId: 5,
        fieldName: 'useOfRealEstateReflectedInTheAppraisal',
        isTextField: true,
        lastTopicId: 5,
        selected: true
      }, {
        topicId: 6,
        fieldName: 'opinionOfHighestAndBestUse',
        isTextField: true,
        lastTopicId: 6,
        selected: true
      }
    ]);
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Delete current row event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public deleteCurrentRow(rows: any[], section: string): void {
    if (section === 'use') {
      this.setTextAndListSettings(this.textAndListSectionNameUse, rows);
      this.deleteCurrentRowSubjectUse.next();
    }
  }

  /**
   * Add row event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public addRow(rows: any[], section: string): void {
    if (section === 'use') {
      const unselectedFields = this.rowsUse.filter(item => !item.selected);
      if (unselectedFields.length > 0) {
        this.setTextAndListSettings(this.textAndListSectionNameUse, rows);
        this.addRowSubjectUse.next({
          topicId: this.getTopicId(section),
          fieldName: null,
          isTextField: true,
          isNumericField: false,
          isDataField: false,
          lastTopicId: this.getTopicId(section),
          selected: true
        });
      }
    }
  }

  /**
   * Move current row up event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public moveCurrentRowUp(rows: any[], section: string): void {
    if (section === 'use') {
      this.setTextAndListSettings(this.textAndListSectionNameUse, rows);
      this.moveCurrentRowUpSubjectUse.next();
    }
  }

  /**
   * Move current row down event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public moveCurrentRowDown(rows: any[], section: string): void {
    if (section === 'use') {
      this.setTextAndListSettings(this.textAndListSectionNameUse, rows);
      this.moveCurrentRowDownSubjectUse.next();
    }
  }

  /**
   * Data changed event handler
   * @param rows {any[]} Rows
   */
  public onDataChanged(rows: any[]): void {
    this.setTextAndListSettings(this.textAndListSectionNameUse, this.rowsUse);
    this.setDirtyFlag(true);
  }

  /**
   * Get topic Id
   * @param section {string} Section name
   */
  private getTopicId(section: string) {
    if (section === 'use') {
      const unselectedFields = this.rowsUse.filter(item => !item.selected);
      return Math.min(...unselectedFields.map(item => item.topicId));
    }
  }

}
